import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import Wrapper from '../elements/wrapper';
import CollectionGridItem from './collection-grid-item';
import CollectionFilter from './collection-filter';

import { media } from '../helpers';
import CollectionPromoItem from './collection-promo-item';
import CollectionBreadCrumb from './collection-breadcrumb';

import X from '../../images/icons/x.inline.svg';

const CollectionGridContainer = styled.div`
    padding: 70px 0;
    .collection-outer-container {
        ${media.medium`max-width: 80vw; display: flex;`}
        .filter-container {
            flex: 1;

            &.desktop-filter {
                .refine-filter-container {
                    display: none;
                    ${media.medium`display: block;`}
                }
            }
        }

        .collection-grid-wrapper {
            flex: 3;

            .active-filters-container {
                margin-bottom: 40px;
            }

            > .inner-wrap {
                display: flex;
                flex-wrap: wrap;
                > * {
                    flex: 0 0 50%;
                    ${media.medium`flex: 0 0 50%;`}
                    ${media.laptop`flex: 0 0 33.33%;`}
                }
            }
        }
    }
    .mobile-toggle {
        ${media.medium`display: none;`}
        text-align: right;
        button {
            padding: 0;
            background-color: transparent;
            color: #000;
            font-size: 0.9rem;
            text-decoration: underline;
            border: none;
        }
    }
`;

const CollectionGrid = ({
    products,
    collection,
    collectionCaption,
    promoOne,
    promoTwo,
}) => {
    // master filter state
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [isMobileFilterActive, setIsMobileFilterActive] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        pattern: [],
        recycled_options: [],
        kids_options: [],
        style: [],
    });

    useEffect(() => {
        setFilteredProducts(products);
    }, []);

    function handleFilterOpen() {
        setIsMobileFilterActive(true);
    }

    const GlobalStyle = createGlobalStyle`
body, html {
overflow-y: ${isMobileFilterActive ? 'hidden' : ''};
}`;

    return (
        <CollectionGridContainer>
            <GlobalStyle />
            <CollectionBreadCrumb
                collection={collection}
                collectionCaption={collectionCaption}
                collectionCount={filteredProducts.length}
                setActiveFilters={setActiveFilters}
                activeFilters={activeFilters}
                isMobileFilterActive={isMobileFilterActive}
                setIsMobileFilterActive={setIsMobileFilterActive}
            />

            <Wrapper className="collection-outer-container">
                <Wrapper className="filter-container desktop-filter">
                    <CollectionFilter
                        allProducts={products}
                        filteredProducts={filteredProducts}
                        setFilteredProducts={setFilteredProducts}
                        setActiveFilters={setActiveFilters}
                        activeFilters={activeFilters}
                    />
                    <div className="mobile-toggle">
                        <button onClick={() => handleFilterOpen()}>
                            Refine
                        </button>
                    </div>
                </Wrapper>

                <AnimatePresence>
                    {isMobileFilterActive && (
                        <MobileFilterContainer
                            key="content"
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                                open: { transform: 'translate3d(0%, 0, 0)' },
                                collapsed: {
                                    transform: 'translate3d(100%, 0, 0)',
                                },
                            }}
                            transition={{
                                duration: 0.5,
                            }}
                        >
                            <div className="close-btn">
                                <button
                                    onClick={() =>
                                        setIsMobileFilterActive(false)
                                    }
                                >
                                    <X />
                                </button>
                            </div>
                            <div className="scroll-container">
                                <CollectionFilter
                                    allProducts={products}
                                    filteredProducts={filteredProducts}
                                    setFilteredProducts={setFilteredProducts}
                                    setActiveFilters={setActiveFilters}
                                    activeFilters={activeFilters}
                                />
                                <div className="view-results">
                                    <button
                                        onClick={() =>
                                            setIsMobileFilterActive(false)
                                        }
                                    >
                                        View {filteredProducts.length} Products
                                    </button>
                                </div>
                            </div>
                        </MobileFilterContainer>
                    )}
                </AnimatePresence>

                <Wrapper className="collection-grid-wrapper">
                    <div className="active-filters-container"></div>
                    <div className="inner-wrap">
                        {filteredProducts &&
                            filteredProducts.map((product, index) => {
                                if (
                                    promoOne &&
                                    promoOne.promoPosition - 1 === index
                                ) {
                                    return (
                                        <>
                                            <CollectionPromoItem
                                                promo={promoOne}
                                            />
                                            <CollectionGridItem
                                                key={index}
                                                product={product}
                                                collectionHandle={
                                                    collection.handle
                                                }
                                            />
                                        </>
                                    );
                                } else if (
                                    promoTwo &&
                                    promoTwo.promoPosition - 1 === index
                                ) {
                                    return (
                                        <>
                                            <CollectionPromoItem
                                                promo={promoTwo}
                                            />
                                            <CollectionGridItem
                                                key={index}
                                                product={product}
                                                collectionHandle={
                                                    collection.handle
                                                }
                                            />
                                        </>
                                    );
                                } else {
                                    return (
                                        <>
                                            <CollectionGridItem
                                                key={index}
                                                product={product}
                                                collectionHandle={
                                                    collection.handle
                                                }
                                            />
                                        </>
                                    );
                                }
                            })}
                    </div>
                </Wrapper>
            </Wrapper>
        </CollectionGridContainer>
    );
};

const MobileFilterContainer = styled(motion.div)`
    position: fixed;
    top: 0;
    height: 100vh;
    background: #f2f0eb;
    left: 0;
    width: 100%;
    z-index: 6000;

    .close-btn {
        position: absolute;
        right: 10px;
        top: 10px;

        button {
            background-color: transparent;
            border: none;
            padding: 10px;
            svg {
                width: 35px;
                height: 35px;
            }
        }
    }

    .scroll-container {
        max-height: 80vh;
        overflow-y: scroll;
        margin-top: 110px;
        padding: 0px 30px 0;
    }

    .filter-linklist {
        display: none;
    }
    .refine-filter-container {
        h4 {
            display: none;
        }

        .clear-btn {
            text-align: right;
            padding-bottom: 15px;
            display: block;
            button {
                border: none;
                background-color: transparent;
                text-decoration: underline;
                font-size: 0.9rem;
            }
        }
    }

    .view-results {
        padding: 30px 0 0;
        text-align: center;

        button {
            background-color: #f74d33;
            color: #fff;
            font-weight: bold;
            width: 100%;
            padding: 10px 0;
            border: 1px solid #f74d33;
            border-radius: 5px;
        }
    }
`;

export default CollectionGrid;
